import { useState } from 'react';
import axios from 'axios';
import { Send, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COMPANY_SIZES = [
  '1–10 employees',
  '11–50 employees',
  '51–200 employees',
  '201–1 000 employees',
  '1 000+ employees',
];

/**
 * Inline lead-capture form for the public Landing page.
 * Posts to /api/leads (no auth) and shows a success state on submit.
 */
const LeadCaptureForm = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    company_size: '',
    notes: '',
    website: '', // honeypot — rendered hidden
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const setField = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: typeof e === 'string' ? e : e.target.value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      setError('Please add your name and email.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/leads`, form);
      setDone(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        className="rounded-xl border border-white/15 bg-white/5 p-8 text-center"
        data-testid="lead-form-success"
      >
        <div className="w-12 h-12 mx-auto rounded-full bg-[#16a34a]/20 flex items-center justify-center mb-3">
          <CheckCircle2 className="h-6 w-6 text-[#16a34a]" />
        </div>
        <h3 className="text-xl font-semibold text-white">Thanks — we'll be in touch.</h3>
        <p className="text-white/70 text-sm mt-2">
          A member of the team will reach out to <strong className="text-white">{form.email}</strong> within one business day.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="rounded-xl border border-white/15 bg-white/5 p-6 md:p-8 space-y-4 text-left"
      data-testid="lead-form"
    >
      {/* Honeypot — hidden from real users, bots will fill it */}
      <div style={{ position: 'absolute', left: '-9999px', height: 0, width: 0, overflow: 'hidden' }} aria-hidden="true">
        <label>
          Website
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={form.website}
            onChange={setField('website')}
          />
        </label>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Your name" required>
          <input
            type="text"
            value={form.name}
            onChange={setField('name')}
            required
            autoComplete="name"
            placeholder="Jane Smith"
            className="lead-input"
            data-testid="lead-name"
          />
        </Field>
        <Field label="Work email" required>
          <input
            type="email"
            value={form.email}
            onChange={setField('email')}
            required
            autoComplete="email"
            placeholder="jane@yourcompany.com"
            className="lead-input"
            data-testid="lead-email"
          />
        </Field>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Company">
          <input
            type="text"
            value={form.company}
            onChange={setField('company')}
            autoComplete="organization"
            placeholder="Acme Facilities Ltd"
            className="lead-input"
            data-testid="lead-company"
          />
        </Field>
        <Field label="Company size">
          <Select value={form.company_size} onValueChange={setField('company_size')}>
            <SelectTrigger
              className="h-11 bg-white/5 border-white/15 text-white hover:bg-white/10 data-[placeholder]:text-white/50"
              data-testid="lead-size"
            >
              <SelectValue placeholder="Select size" />
            </SelectTrigger>
            <SelectContent>
              {COMPANY_SIZES.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </div>

      <Field label="What would you like to discuss?">
        <textarea
          rows={3}
          value={form.notes}
          onChange={setField('notes')}
          placeholder="Number of sites, current tooling, timeline…"
          className="lead-input resize-y min-h-[88px]"
          data-testid="lead-notes"
        />
      </Field>

      {error && (
        <p
          className="text-sm text-red-400"
          data-testid={error.includes('Too many') ? 'lead-rate-limited' : 'lead-error'}
        >
          {error}
        </p>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-1">
        <p className="text-[11px] text-white/50">
          We'll only use your details to respond. No marketing list.
        </p>
        <Button
          type="submit"
          size="lg"
          disabled={submitting}
          className="bg-[#ff6600] hover:bg-[#e65a00] text-white h-11 px-6"
          data-testid="lead-submit"
        >
          {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
          {submitting ? 'Sending…' : 'Request a demo'}
        </Button>
      </div>

      <style>{`
        .lead-input {
          width: 100%;
          height: 44px;
          padding: 0 12px;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.15);
          border-radius: 6px;
          color: #fff;
          font-size: 14px;
          outline: none;
          transition: border-color 0.15s, background-color 0.15s;
        }
        .lead-input::placeholder { color: rgba(255,255,255,0.4); }
        .lead-input:focus {
          border-color: #ff6600;
          background: rgba(255,255,255,0.08);
        }
        textarea.lead-input { padding-top: 10px; height: auto; }
      `}</style>
    </form>
  );
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="text-xs font-semibold uppercase tracking-wider text-white/70">
      {label}{required && <span className="text-[#ff6600]"> *</span>}
    </span>
    <div className="mt-1.5">{children}</div>
  </label>
);

export default LeadCaptureForm;
